import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import Fuse from 'fuse.js';
import FilterRange from '@components/ui_filter-range/component';
import Checkbox from '@components/ui_checkbox/component';
import GenreNavItem from '@components/sec_search-results/_Filter/_GenreNavItem/component';
import { Button } from 'audamatic-ui';
import Portal from '@components/ui_portal/component';
import Icon from '@components/base_svg-icon/component';
import FilterGroup from '@components/ui_filter-group/component';
import Context from '@context/search-results';
import { formatNumber } from '@utils';
import Text from '@text';
import { t } from '@utils/i18n';

import './styles.scss';

class FilterItem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: props.expanded,
      overflowHidden: true,
      showModal: false,
      hasChanged: false,
      hasScrolled: false,
      hasOverflow: false,
      query: '',
      maxOptionLength: 50,
    };
    this.listRef = createRef();
  }

  componentDidMount() {
    this.props.store.events.subscribe('closeModal', () => this.toggleModal({ forceClose: true })());
  }

  /**
   * Check if the content of the filter-modal has an overflow. If so, show an inner shadow.
   * Also check if the modal should load more options when the user has scrolled to the bottom.
   */
  checkForOverflow = () => {
    const { hasOverflow, hasScrolled } = this.state;
    const { scrollHeight, clientHeight, scrollTop } = this.listRef.current || {};
    const { options = [] } = this.props.filter || {};

    const overflows = scrollHeight > clientHeight;
    const hasReachedEnd = (scrollTop + clientHeight) === scrollHeight;
    if (!hasOverflow && overflows) {
      this.setState({ hasOverflow: true });
    }
    if (hasOverflow && hasScrolled && hasReachedEnd) {
      this.setState({ hasOverflow: false });
    }

    const offset = 300; // px
    const scrolledToBottom = clientHeight + scrollTop + offset > scrollHeight;
    const hasTooManyOptions = options.length > 50;
    if (hasTooManyOptions && scrolledToBottom && !hasReachedEnd) {
      this.setState(prevState => ({ maxOptionLength: prevState.maxOptionLength + 50 }));
    }
  };

  /**
   * Check if the user has scrolled trough the filter options
   * if so, show an inner shadow below the search bar.
   */
  checkScrollPosition = () => {
    if (this.state.hasScrolled && this.listRef.current.scrollTop === 0) {
      this.setState({ hasScrolled: false });
    }
    if (!this.state.hasScrolled && this.listRef.current.scrollTop > 0) {
      this.setState({ hasScrolled: true });
    }
  };

  /**
   * Show the filter modal and add the event listeners after
   * the modal has mounted. Also, lock the document from scrolling.
   */
  toggleModal = ({ forceClose, showResults } = {}) => () => {
    if (showResults) {
      this.props.store.modal.closeModal();
    }

    if (forceClose && this.context.closeFilter) {
      this.context.closeFilter();
    }

    if (this.listRef.current) {
      this.listRef.current.removeEventListener('scroll', this.checkScrollPosition);
      this.listRef.current.removeEventListener('scroll', this.checkForOverflow);
      window.removeEventListener('resize', this.checkForOverflow);
    }

    this.setState(prevState => ({
      showModal: forceClose ? false : !prevState.showModal,
      hasChanged: false,
      hasScrolled: false,
      query: '',
    }), () => {
      setTimeout(() => {
        if (this.state.showModal && this.listRef.current) {
          this.listRef.current.addEventListener('scroll', this.checkScrollPosition);
          this.listRef.current.addEventListener('scroll', this.checkForOverflow);
          window.addEventListener('resize', this.checkForOverflow);
          this.checkForOverflow();
        }
      }, 200);

      document.documentElement.style.overflowY = this.state.showModal ? 'hidden' : '';
    });
  };

  toggleOverflow = () => {
    this.setState(prevState => ({ overflowHidden: !prevState.overflowHidden }));
  };

  toggleFilterGroup = () => {
    this.setState(prevState => ({ expanded: !prevState.expanded }));
  };

  handleCheckboxToggle = ({ filterId, item, close }) => (selected) => {
    this.props.handleCheckboxToggle({ filterId, item, close, selected, load: !this.state.showModal });
    this.setState({ hasChanged: true });
  };

  handleQueryInput = (e) => {
    this.setState({ query: e.target.value });
  };

  /**
   * Render checkbox filter option (default)
   *
   * @param {string} filterId
   * @param {FilterObject} item
   * @returns {HTMLElement}
   */
  renderCheckboxes = (filterId, item) => {
    const { selected, id, count, value = '', displayName } = item;
    let checked = selected;
    if (this.props.activeFilters) {
      checked = !!this.props.activeFilters.find(filter => filter.id === id);
    }
    const itemName = value.startsWith('odssearch') ? t(value) : value;
    return (
      <Checkbox
        key={filterId + itemName}
        groupId={filterId}
        id={itemName}
        label={displayName || itemName}
        onChange={this.handleCheckboxToggle({ filterId, item })}
        checked={checked}
        count={formatNumber(Number(count))}
        variant="styled"
      />
    );
  };

  /**
   * Render category specific filter option
   *
   * @param {string} filterId
   * @param {FilterObject} item
   * @returns {HTMLElement}
   */
  renderGenreNavigation = (filterId, item) => {
    return (
      <GenreNavItem
        key={filterId + item.value}
        id={item.value}
        label={item.displayName}
        onChange={this.handleCheckboxToggle({ filterId, item, close: true })}
        checked={item.selected}
        count={formatNumber(Number(item.count))}
      />
    );
  };

  /**
   * Determine the type of show-more button to render (if any)
   *
   * @returns {HTMLElement}
   */
  renderShowMoreButton = () => {
    const { overflowHidden, expanded } = this.state;
    const { options = [], displayName = '' } = this.props.filter || {};
    const dataTestId = `toggle-filter${displayName}`;
    if (!expanded || options.length <= 5) {
      return null;
    }

    if (options.length > 7) {
      return (
        <a
          onClick={this.toggleModal()}
          className="filter-group-toggle-wrapper"
          data-test-id={dataTestId}>
          <div className="toggle-icon">
            <Icon name="ico-search"/>
          </div>
          <div className="filter-group-toggle-more">
            <Text>search.result.filter.showMore</Text>
          </div>
        </a>
      );
    }

    return (
      <a
        onClick={this.toggleOverflow}
        className={`filter-group-toggle-wrapper ${overflowHidden ? 'closed' : 'open'}`}
        data-test-id={dataTestId}>
        <div className="toggle-icon">
          <Icon name="ico-arrow-down"/>
        </div>
        <div className="filter-group-toggle-more">
          {t(overflowHidden ? 'search.result.filter.showMore' : 'search.result.filter.showLess')}
        </div>
      </a>
    );
  };

  /**
   * Determine which option variant to render based on filter ID
   *
   * @param {string} filterId
   * @param {object} option
   * @returns {HTMLElement}
   */
  filterOptionVariant = (filterId, option) => {
    const variantMap = {
      CategoryUUIDLevelMulti: this.renderGenreNavigation(filterId, option),
    };
    return variantMap[filterId] ||  this.renderCheckboxes(filterId, option);
  };

  /**
   * Get the (sub)set of filter options, which are initially visible.
   * Include all active options set in the modal.
   *
   * @returns {HTMLElement}
   */
  get visibleFilterOptions() {
    const { expanded, overflowHidden } = this.state;
    const { id, options = [] } = this.props.filter || {};
    let numberOfOptions =  0;
    if (expanded) {
      numberOfOptions = options.length > 5 ? 4 : 5;
    }
    if (!overflowHidden) {
      numberOfOptions = 7;
    }

    let initialOptions = [...options].slice(0, numberOfOptions);
    const numberOfActiveInitialOptions = initialOptions.filter(option => option.selected).length;

    /**
     * For categories we hide the active selections, but we should still show 4 or 5 items
     * so we increment the number of initial options
     */
    if (id === 'CategoryUUIDLevelMulti' && numberOfActiveInitialOptions > 0) {
      initialOptions = [...options].slice(0, numberOfOptions + numberOfActiveInitialOptions);
    }

    const activeOptions = [...options].filter((option) => {
      const isInitialOptions = initialOptions.find((initialOption) => {
        return option.value === initialOption.value;
      });
      if (isInitialOptions) {
        return false;
      }
      return option.selected === true;
    });

    return [...initialOptions, ...activeOptions]
      .map((option) => {
        return this.filterOptionVariant(id, option);
      });
  }

  /**
   * Return an array of options to show inside the modal
   * based on the search query, if any.
   *
   * @returns {array}
   */
  get visibleOptions() {
    const { options = [] } = this.props.filter || {};
    const { query } = this.state;

    let visibleOptions = [...options];
    if (query) {
      const fuse = new Fuse(options, {
        shouldSort: true,
        threshold: 0.2,
        location: 0,
        distance: 100,
        maxPatternLength: 12,
        minMatchCharLength: 1,
        keys: ['displayName'],
      });
      const results = fuse.search(query);
      visibleOptions = results.map(result => result.item);
    }
    return visibleOptions;
  }

  /**
   * Get all visible filter options and sorted by name asc
   * If it starts with a new letter, add a letterhead
   *
   * @returns {HTMLElement}
   */
  get allFilterOptions() {
    const { query, maxOptionLength } = this.state;
    const { id } = this.props.filter || {};
    let sortedOptions = this.visibleOptions;
    const maxNumberOfOptions = query ? 10 : maxOptionLength;
    if (!query) {
      sortedOptions = this.visibleOptions.sort((a, b) => {
        if (a.displayName?.toUpperCase() < b.displayName?.toUpperCase()) {
          return -1;
        } else if (a.displayName?.toUpperCase() > b.displayName?.toUpperCase()) {
          return 1;
        }
        return 0;
      });
    }

    const letterHeads = [];
    return sortedOptions.slice(0, maxNumberOfOptions).map((option) => {
      let showLetterHead = false;
      let letter = option.displayName?.charAt(0).toUpperCase();
      if (!letter.match(/[A-Z]/g)) {
        letter = '#';
      }

      if (!letterHeads.includes(letter)) {
        showLetterHead = true;
        letterHeads.push(letter);
      }
      return (
        <>
          {showLetterHead && !query && (
            <div className="filter-letterhead">{letter}</div>
          )}
          {id ? this.filterOptionVariant(id, option) : null}
        </>
      );
    });
  }

  /**
   * Return all or only the visible filter options based on param
   * Return filterRange component in filter type is price.
   *
   * @param {boolean} all
   * @returns {HTMLElement}
   */
  renderFilterItemOptions = (all) => {
    const { expanded } = this.state;
    const { id = '', options = [] } = this.props.filter || {};

    if (!expanded || !options.length) {
      return null;
    }

    if (all) {
      return this.allFilterOptions;
    }

    if (id.toLowerCase() === 'regularprice') {
      const { currentMin, currentMax, min, max } = options[0];
      return (
        <FilterRange
          selectedMin={currentMin || ''}
          selectedMax={currentMax || ''}
          min={min}
          max={max}
          filter={JSON.stringify(this.props.filters)}
          callBack={({ currentMin, currentMax }) => this.props.priceChangedCallBack(id, {
            min, max, currentMin, currentMax,
          })}
        />);
    }

    return this.visibleFilterOptions;
  };

  /**
   * Render all options including search bar inside this custom modal
   *
   * @returns {HTMLElement}
   */
  renderFilterModal() {
    const { filter = {} } = this.props;
    const { displayName } = filter;
    const { showModal, hasChanged, hasScrolled, hasOverflow } = this.state;
    if (!showModal) {
      return null;
    }

    return (
      <Portal className="filter-portal">
        <div className="filter-modal-container">
          <div className="curtain" onClick={this.toggleModal()}/>
          <div className="filter-modal-wrapper">
            <div className="filter-modal-header">
              <span>
                <div className="back filter-modal-header-icon" onClick={this.toggleModal()}>
                  <Icon name="ico-arrow-left"/>
                </div>
                {displayName}
              </span>
              <div className="close filter-modal-header-icon" onClick={this.toggleModal({ showResults: true, forceClose: true })}>
                <Icon name="ico-error"/>
              </div>
            </div>
            <div className={`filter-modal-search ${hasScrolled ? 'scrolled' : ''}`}>
              <div>
                <input placeholder={t('search.filter.search.placeholder')} onChange={this.handleQueryInput} data-test-id="input-filterSearch"/>
                <div className="filter-modal-search-icon">
                  <Icon name="ico-search" />
                </div>
              </div>
            </div>
            <div className={`filter-modal-body ${hasOverflow ? 'overflows' : ''}`} ref={this.listRef}>
              {this.renderFilterItemOptions(true)}
              {!this.visibleOptions.length && (
                <div><Text>search.filter.noResults</Text></div>
              )}
            </div>
            <div className={`filter-modal-footer ${hasChanged ? 'visible' : ''}`}>
              <Button
                variant="primary"
                size="large"
                onClick={this.toggleModal({ showResults: true, forceClose: true })}
                data-test-id="button-filterModal"
              >
                <Text>search.filter.showResults</Text>
              </Button>
            </div>
          </div>
        </div>
      </Portal>
    );
  }

  render() {
    const { hasTextKey } = this.props;
    const { displayName } = this.props.filter || {};
    const { expanded } = this.state;
    const title = !hasTextKey ? displayName : (<Text>{displayName}</Text>);

    return (
      <>
        <FilterGroup expanded={expanded} title={title}>
          {this.renderFilterItemOptions()}
          {this.renderShowMoreButton()}
        </FilterGroup>
        {this.renderFilterModal()}
      </>
    );
  }
}

FilterItem.contextType = Context;
FilterItem.propTypes = {
  filters: PropTypes.array,
  activeFilters: PropTypes.array,
  filter: PropTypes.object.isRequired,
  expanded: PropTypes.bool,
  store: PropTypes.object.isRequired,
  handleCheckboxToggle: PropTypes.func,
  priceChangedCallBack: PropTypes.func,
  // Whether or not the displayName is passed as a string or as loc. text key
  hasTextKey: PropTypes.bool,
};

export default FilterItem;
